import { ChangeEvent, useEffect, useState } from 'react';
import {
  changeAccLanguage,
  changeAccStealth,
  checkCurrentMessageUsage,
  createBillingInfo,
  getAccountSettings,
  getAuthUrl,
  getBillingInfo,
  getConfig,
  getEnvironment,
  getIntegrationToken,
  getOptInMessage,
  getSubdomain,
  getSubscriptionData,
  putConfig,
  putSubdomain,
  removeServiceAuthRequest,
  setOptInMessageStatusService,
  resetOptInMessage,
  setupZendesk,
  updateActionsConfigProperty,
  updateBillingInfo,
  updateCustomConfig,
  updateIsAngryCheckDisabled,
  updateIsProcessingDisabled,
  updateProcessingConfig,
  updateSubscriptionConfig,
  updateAccountMultiAccountConfigUser,
} from '../../services/settingsServices';
import { ActionsConfig, TrackingConfigType } from '../../interfaces/actions';
import {
  AccountConfig,
  BillingInfo,
  CustomConfigType,
  MultiAccountConfigType,
  ProcessingConfigType,
  SubscriptionData,
} from '../../interfaces/account';
import { NullString } from '../../interfaces/general';
import { ServiceType } from '../../interfaces/integrations';
import isObjectEmpty from '../../utils/isObjectEmpty';
import { ActionType } from '../../constants/action';
import { ShopifyBillingSettings } from '../shopify/useShopifyBillingSettings';
import { ChangeMultiAccountConfig } from '../admin/useAdmin';

export interface ChangeActionsConfig {
  (key: string, value: boolean | string | TrackingConfigType): Promise<
    | {
        error?: string;
        success?: boolean;
      }
    | undefined
  >;
}

export interface UseAccountSettings {
  (enqueueSnackbar: (message: string, options?: any) => void): {
    accountLoading: boolean;
    subdomain: string | null;
    apiEmailInput: string;
    apiKeyInput: string;
    apiKey: string;
    configSaveErr: string | null;
    configSaveLoading: boolean;
    configSaveSuccess: boolean;
    subdomainDraft: string;
    setSubdomainDraft: (subdomain: string) => void;
    modalLoading: boolean;
    showSubModal: boolean;
    setShowSubModal: (show: boolean) => void;
    connectorLoading: boolean;
    confirmationModalOpen: boolean;
    setConfirmationModalOpen: (open: boolean) => void;
    billingLoading: boolean;
    billingInfo: BillingInfo | null;
    billingSaveLoading: boolean;
    language: string;
    langSaveLoading: boolean;
    langSaveSuccess: boolean;
    stealthModeOn: boolean;
    setStealthModeOn: (stealthMode: boolean) => void;
    isStealthEmailOn: boolean;
    setIsStealthEmailOn: (isStealthEmail: boolean) => void;
    stealthEmail: string;
    saveStealthError: string | null;
    stealthSaveLoading: boolean;
    stealthSaveSuccess: boolean;
    webhookUrl: string;
    isProcessingDisabled: boolean;
    saveZendeskConfig: () => void;
    handleAddSubdomainClick: () => void;
    handleEditSubdomainClick: () => void;
    handleConnectClick: (service: ServiceType) => void;
    handleCompanyInfoChange: (e: ChangeEvent<HTMLInputElement>) => void;
    handleApiKeyChange: (e: ChangeEvent<HTMLInputElement>) => void;
    handleApiEmailChange: (e: ChangeEvent<HTMLInputElement>) => void;
    handleSaveCompanyInfoClick: () => void;
    changeSelectedLanguage: (event: ChangeEvent<{ value: string }>) => void;
    saveLanguage: SaveLanguage;
    saveStealthModeSettings: (newEmail: string) => void;
    changeIsProcessingDisabled: (isDisabled: boolean) => void;
    getZendeskAuthUrl: () => void;
    zendeskOAuthUrl: string;
    authUrlLoading: boolean;
    authUrlError: string | null;
    hasZendeskToken: boolean;
    refundRequireImage: boolean;
    updateSubscriptionConfigForUser: (config: any) => void;
    actionsConfig: ActionsConfig;
    changeActionsConfig: ChangeActionsConfig;
    enableReplaceExchange: boolean;
    enableSameItemReplacement: boolean;
    vouchersEnabled: boolean;
    manualVoucherApprovalOn: boolean;
    optInMessageEnabled: boolean;
    optInMessage: string;
    changeOptInMessageEnabled: ChangeOptInMessageEnabled;
    handleResetOptInMessage: HandleResetOptInMessage;
    messageUsage: number | null;
    messageUsageLoading: boolean;
    checkMessageUsage: () => void;
    getZendeskSubdomain: () => void;
    isOrderCancelActionOn: boolean;
    isCancelApprovalOn: boolean;
    isShopifyMarket: boolean;
    isAngryCheckDisabled: boolean;
    changeIsAngryCheckDisabled: ChangeIsAngryCheckDisabled;
    shopifyBillingSettings: ShopifyBillingSettings | null;
    processingConfig: ProcessingConfigType | null;
    changeProcessingConfig: ChangeProcessingConfig;
    customConfig: CustomConfigType | null;
    changeCustomConfig: ChangeCustomConfig;
    removeServiceAuth: RemoveServiceAuth;
    multiAccountConfig: MultiAccountConfigType | null;
    changeMultiAccountConfig: ChangeMultiAccountConfig;
  }
}

export interface ChangeOptInMessageEnabled {
  (enabled: boolean, customMessage: string): Promise<{ error?: string; success?: boolean }>;
}

export interface HandleResetOptInMessage {
  (): Promise<{ success?: boolean; error?: string }>;
}

export interface ChangeIsAngryCheckDisabled {
  (disabled: boolean): Promise<{ error?: string; success?: boolean }>;
}

export interface ChangeProcessingConfig {
  (key: keyof ProcessingConfigType, value: any): Promise<{ error?: string; success?: boolean }>;
}

export interface ChangeCustomConfig {
  (key: keyof CustomConfigType, value: any): Promise<{ error?: string; success?: boolean }>;
}

export interface SaveLanguage {
  (newLang?: string): void;
}

export interface RemoveServiceAuth {
  (serviceName: string): Promise<{success?: boolean, error?: string}>;
}

const useAccountSettings: UseAccountSettings = (enqueueSnackbar) => {
  const [accountLoading, setAccountLoading] = useState<boolean>(true);
  const [subdomain, setSubdomain] = useState<NullString>(null);
  const [apiEmailInput, setApiEmailInput] = useState('');
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [configDict, setConfigDict] = useState<AccountConfig | null>(null);
  const [configSaveErr, setConfigSaveErr] = useState<NullString>(null);
  const [configSaveLoading, setConfigSaveLoading] = useState(false);
  const [configSaveSuccess, setConfigSaveSuccess] = useState(false);
  const [subdomainDraft, setSubdomainDraft] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [showSubModal, setShowSubModal] = useState(false);
  const [connectorLoading, setConnectorLoading] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [billingLoading, setBillingLoading] = useState<boolean>(false);
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);
  const [billingSaveLoading, setBillingSaveLoading] = useState(false);
  const [language, setLanguage] = useState('english');
  const [langSaveLoading, setLangSaveLoading] = useState(false);
  const [langSaveSuccess, setLangSaveSuccess] = useState(false);
  const [langSaveError, setLangSaveError] = useState<NullString>(null);
  const [stealthModeOn, setStealthModeOn] = useState(false);
  const [stealthEmail, setStealthEmail] = useState('');
  const [isStealthEmailOn, setIsStealthEmailOn] = useState(false);
  const [saveStealthError, setSaveStealsError] = useState<NullString>(null);
  const [stealthSaveLoading, setStealthSaveLoading] = useState(false);
  const [stealthSaveSuccess, setStealthSaveSuccess] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [isProcessingDisabled, setIsProcessingDisabled] = useState(false);
  const [zendeskOAuthUrl, setZendeskOAuthUrl] = useState<string>('');
  const [authUrlLoading, setAuthUrlLoading] = useState(false);
  const [authUrlError, setAuthUrlError] = useState<NullString>(null);
  const [hasZendeskToken, setHasZendeskToken] = useState(false);
  const [actionsConfig, setActionsConfig] = useState<ActionsConfig>({});
  const [enableReplaceExchange, setEnableReplaceExchange] = useState(false);
  const [enableSameItemReplacement, setEnableSameItemReplacement] = useState(false);
  const [refundRequireImage, setRefundRequireImage] = useState(false);
  const [vouchersEnabled, setVouchersEnabled] = useState(false);
  const [manualVoucherApprovalOn, setManualVoucherApprovalOn] = useState(false);
  const [isOrderCancelActionOn, setIsOrderCancelActionOn] = useState<boolean>(false);
  const [isCancelApprovalOn, setIsCancelApprovalOn] = useState<boolean>(false);
  const [optInMessageEnabled, setOptInMessageEnabled] = useState(false);
  const [optInMessage, setOptInMessage] = useState('');
  const [messageUsage, setMessageUsage] = useState<number | null>(null);
  const [messageUsageLoading, setMessageUsageLoading] = useState(false);
  const [isShopifyMarket, setIsShopifyMarket] = useState<boolean>(false);
  const [isAngryCheckDisabled, setIsAngryCheckDisabled] = useState<boolean>(false);
  const [shopifyBillingSettings, setShopifyBillingSettings] = useState<ShopifyBillingSettings | null>(null);
  const [processingConfig, setProcessingConfig] = useState<ProcessingConfigType | null>(null);
  const [customConfig, setCustomConfig] = useState<CustomConfigType | null>(null);
  const [multiAccountConfig, setMultiAccountConfig] = useState<MultiAccountConfigType | null>(null);
  
  useEffect(() => {
    getZendeskSubdomain();
    getClientBillingInfo();
    getZendeskConfig();
    loadAccountSettings();
    getZendeskToken();
    fetchShopifyBillingSettings();
    localStorage.removeItem('connected');
  }, []);

  const loadAccountSettings = async () => {
    try {
      setAccountLoading(true);
      const res = await getAccountSettings();
      const envRes = await getEnvironment();
      const environment = envRes.data.environment;
      if (res.data?.user_id) {
        const webhookUrl =
          environment === 'dev'
            ? `https://api-dev.vennie.tech/dev/v1/zendesk/ticket/${res.data.user_id}`
            : `https://processing.vennie.tech/v1/zendesk/webhook/${res.data.user_id}`;
        setWebhookUrl(webhookUrl);
      }
      if (res.data?.language) {
        setLanguage(res.data.language);
      }
      if ('stealth_mode' in res.data) {
        setStealthModeOn(res.data.stealth_mode);
      }
      if ('stealth_email' in res.data) {
        setStealthEmail(res.data.stealth_email);
      }
      if ('is_processing_disabled' in res.data) {
        setIsProcessingDisabled(res.data.is_processing_disabled);
      }
      if ('is_stealth_email_on' in res.data) {
        setIsStealthEmailOn(res.data.is_stealth_email_on);
      }
      if ('actions_config' in res.data && res.data.actions_config) {
        parseActionsConfig(res.data.actions_config);
      }
      if ('opt_in_message_enabled' in res.data) {
        setOptInMessageEnabled(res.data.opt_in_message_enabled);
      }
      if (
        'opt_in_message' in res.data &&
        typeof res.data.opt_in_message === 'string' &&
        res.data.opt_in_message.length > 5
      ) {
        setOptInMessage(res.data.opt_in_message);
      } else {
        await loadOptInMessage();
      }
      if ('is_shopify_market' in res.data) {
        setIsShopifyMarket(res.data.is_shopify_market);
      }
      if ('is_angry_check_disabled' in res.data) {
        setIsAngryCheckDisabled(res.data.is_angry_check_disabled);
      }
      if ('processing_config' in res.data) {
        setProcessingConfig(res.data.processing_config);
      }
      if ('custom_config' in res.data) {
        setCustomConfig(res.data.custom_config);
      }
      if ('multi_account_config' in res.data) {
        setMultiAccountConfig(res.data.multi_account_config);
      }

      setTimeout(() => {
        setAccountLoading(false);
      }, 100);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchShopifyBillingSettings = async () => {
    try {
      setBillingLoading(true);
      const response = await getSubscriptionData();
  
      if (response.status === 200) {
        setShopifyBillingSettings(response.data.subscription_data);
      }
      setBillingLoading(false);
    } catch (error) {
      setBillingLoading(false);
    }
  };

  function parseActionsConfig(actionsConfig: ActionsConfig) {
    setActionsConfig(actionsConfig);
    if (
      ActionType.ENABLE_REFUND_REQUIRE_IMAGE in actionsConfig &&
      typeof actionsConfig.refund_require_image === 'boolean'
    ) {
      setRefundRequireImage(actionsConfig.refund_require_image);
    }
    if (ActionType.ENABLE_VOUCHERS in actionsConfig && typeof actionsConfig.vouchers_enabled === 'boolean') {
      setVouchersEnabled(actionsConfig.vouchers_enabled);
    }
    if (
      ActionType.ENABLE_MANUAL_VOUCHER_APPROVAL in actionsConfig &&
      typeof actionsConfig.manual_voucher_approval_on === 'boolean'
    ) {
      setManualVoucherApprovalOn(actionsConfig.manual_voucher_approval_on);
    }
    if (
      ActionType.ENABLE_REPLACE_EXCHANGE in actionsConfig &&
      typeof actionsConfig.enable_replace_exchange === 'boolean'
    ) {
      setEnableReplaceExchange(actionsConfig.enable_replace_exchange);
    }
    if (
      ActionType.ENABLE_SAME_ITEM_REPLACEMENT in actionsConfig &&
      typeof actionsConfig.enable_same_item_replacement === 'boolean'
    ) {
      setEnableSameItemReplacement(actionsConfig.enable_same_item_replacement);
    }
    if (ActionType.ENABLE_CANCEL_ORDER in actionsConfig && typeof actionsConfig.enable_cancel_order === 'boolean') {
      setIsOrderCancelActionOn(actionsConfig.enable_cancel_order);
    }
    if (
      ActionType.ENABLE_CANCEL_APPROVAL in actionsConfig &&
      typeof actionsConfig.enable_cancel_approval === 'boolean'
    ) {
      setIsCancelApprovalOn(actionsConfig.enable_cancel_approval);
    }
  }

  const getZendeskSubdomain = async () => {
    try {
      const res = await getSubdomain();
      setSubdomain(res.data?.subdomain);
    } catch (err) {}
  };

  const getZendeskConfig = async () => {
    try {
      const loadRes = await getConfig();

      if (loadRes.status === 200) {
        if (loadRes.data?.config) {
          setConfigDict(loadRes.data.config);

          if (loadRes.data?.config?.api_key) {
            setApiKey(loadRes.data?.config?.api_key);
          }
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const saveZendeskConfig = async () => {
    if (configDict) {
      if (apiEmailInput && apiKeyInput) {
        setConfigSaveLoading(true);
        setConfigSaveErr(null);
        const encodedCredentialsBase64 = btoa(`${apiEmailInput}/token:${apiKeyInput}`);

        const updatedSetup = { ...configDict };
        updatedSetup.api_key = encodedCredentialsBase64;

        const updateRes = await putConfig(updatedSetup);
        setConfigSaveLoading(false);
        if (updateRes.status === 200) {
          setConfigSaveSuccess(true);
          setTimeout(() => {
            setConfigSaveSuccess(false);
          }, 2000);
        } else {
          setConfigSaveErr(updateRes.statusText);
        }
      } else {
        setConfigSaveErr('Input agent email and API key');
      }
    } else {
      setConfigSaveErr('Invalid config');
    }
  };

  const getClientBillingInfo = async () => {
    try {
      setBillingLoading(true);
      const res = await getBillingInfo();
      setBillingLoading(false);
      if (res.data) {
        setBillingInfo(res.data);
      } else {
        setBillingInfo({ empty: true });
      }
    } catch (err) {
      setBillingLoading(false);
      console.log(err);
    }
  };

  const updateClientBillingInfo = async () => {
    try {
      setBillingSaveLoading(true);
      const res = await updateBillingInfo(billingInfo);
      enqueueSnackbar('Info updated!');
      setBillingSaveLoading(false);
      setBillingInfo(res.data);
    } catch (err) {
      enqueueSnackbar('Failed to save!');
      console.log(err);
    }
  };

  const createClientBillingInfo = async () => {
    try {
      setBillingSaveLoading(true);
      const res = await createBillingInfo(billingInfo);
      enqueueSnackbar('Info Saved!');
      setBillingSaveLoading(false);
      getClientBillingInfo();
    } catch (err) {
      enqueueSnackbar('Failed to save!');
      console.log(err);
    }
  };

  const setupZendeskConnector = async () => {
    try {
      await setupZendesk();
    } catch (err) {
      console.log(err);
    }
  };

  const handleAddSubdomainClick = async () => {
    setModalLoading(true);
    try {
      const res = await putSubdomain(subdomainDraft);
      getZendeskSubdomain();
      removeServiceAuth('zendesk_agent');
    } catch (err) {
      console.log(err);
    }
    setModalLoading(false);
    setShowSubModal(false);
  };

  const handleEditSubdomainClick = () => {
    if (subdomain) {
      setSubdomainDraft(subdomain);
      setShowSubModal(true);
    }
  };

  const handleConnectClick = async (service: ServiceType) => {
    setConnectorLoading(true);
    try {
      // Gets authentication url from backend server

      const {
        data: { url }
      } = await getAuthUrl(service);

      // Navigate to consent screen
      const openedWindow = window.open(url, 'Oauth', 'width=800,height=600,resizable,scrollbars');
      window.addEventListener('storage', (event) => {
        const oauthState = localStorage.getItem('connected');
        if (event.key === 'connected' && event.newValue !== null) {
          if (oauthState === 'failed') {
            setConnectorLoading(false);
            clearInterval(intervalInstance);
          } else if (oauthState === 'zendesk_admin') {
            setupZendeskConnector();
          } else if (oauthState === 'zendesk_agent') {
            enqueueSnackbar('Zendesk Connected!');
          }
        }
        localStorage.removeItem('connected');
      });
      const intervalInstance = setInterval(() => {
        if (!!openedWindow && openedWindow?.closed) {
          setConnectorLoading(false);
          clearInterval(intervalInstance);
          localStorage.removeItem('connected');
        }
      }, 5000);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCompanyInfoChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;
    setBillingInfo({ ...billingInfo, [name]: value });
  };

  const handleApiKeyChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setApiKeyInput(value);
  };

  const handleApiEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setApiEmailInput(value);
  };

  const handleSaveCompanyInfoClick = () => {
    if (isObjectEmpty(billingInfo)) {
      createClientBillingInfo();
    } else {
      updateClientBillingInfo();
    }
  };

  const changeSelectedLanguage = (event: ChangeEvent<{ value: string }>) => {
    setLanguage(event.target.value);
    saveLanguage(event.target.value);
  };

  const saveLanguage: SaveLanguage = async (newLang: string | null = null) => {
    try {
      setLangSaveLoading(true);
      const changeRes = await changeAccLanguage(newLang || language);
      setLangSaveLoading(false);

      if (changeRes.status === 200) {
        setLangSaveSuccess(true);
        setTimeout(() => {
          setLangSaveSuccess(false);
        }, 2000);
      } else {
        setLangSaveError('Save failed, try again or contact support');
      }
    } catch (err) {
      setLangSaveLoading(false);
      setLangSaveError(`${err}`);
    }
  };

  const saveStealthModeSettings = async (newEmail: string) => {
    try {
      setStealthSaveLoading(true);
      setStealthEmail(newEmail);

      const changeRes = await changeAccStealth({
        stealthModeOn: stealthModeOn,
        stealthEmail: newEmail,
        isStealthEmailOn: isStealthEmailOn
      });
      const disableProcessingRes = await updateIsProcessingDisabled(isProcessingDisabled);

      setStealthSaveLoading(false);
      if (changeRes.status === 200) {
        setStealthSaveSuccess(true);
        setTimeout(() => {
          setStealthSaveSuccess(false);
        }, 2000);
      } else {
        setSaveStealsError('Save failed, try again or contact support');
      }
    } catch (err) {
      setStealthSaveLoading(false);
      setSaveStealsError(`${err}`);
    }
  };

  const changeIsProcessingDisabled = async (isDisabled: boolean) => {
    setIsProcessingDisabled(isDisabled);
  };

  const getZendeskAuthUrl = async () => {
    try {
      setAuthUrlLoading(true);
      const res = await getAuthUrl('zendesk_agent');
      setAuthUrlLoading(false);
      if (res.status === 200) {
        setZendeskOAuthUrl(res.data.url);
      } else {
        setAuthUrlError(res.data.detail || 'Failed to get auth url');
      }
    } catch (err) {
      console.log(err);
    }
  };

  const getZendeskToken = async () => {
    try {
      const res = await getIntegrationToken('zendesk_agent');
      if (res.status === 200 && res.data.token !== null) {
        setHasZendeskToken(true);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const changeActionsConfig = async (key: string, value: any) => {
    try {
      updateActionsState(key, value);

      const updatedActionsConfig = { ...actionsConfig, [key]: value };
      setActionsConfig(updatedActionsConfig);

      const res = await updateActionsConfigProperty(key, value);

      if (res.status === 200) {
        return {
          success: true
        };
      } else {
        console.error(res);
        return {
          success: false,
          error: res.data.detail
        };
      }
    } catch (err) {
      return {
        success: false,
        error: `${err}`
      };
    }
  };

  const updateActionsState = (key: string, value: any) => {
    if (key === ActionType.ENABLE_REFUND_REQUIRE_IMAGE) {
      setRefundRequireImage(value);
    } else if (key === ActionType.ENABLE_VOUCHERS) {
      setVouchersEnabled(value);
    } else if (key === ActionType.ENABLE_MANUAL_VOUCHER_APPROVAL) {
      setManualVoucherApprovalOn(value);
    } else if (key === ActionType.ENABLE_REPLACE_EXCHANGE) {
      setEnableReplaceExchange(value);
    } else if (key === ActionType.ENABLE_SAME_ITEM_REPLACEMENT) {
      setEnableSameItemReplacement(value);
    } else if (key === ActionType.ENABLE_CANCEL_ORDER) {
      setIsOrderCancelActionOn(value);
    } else if (key === ActionType.ENABLE_CANCEL_APPROVAL) {
      setIsCancelApprovalOn(value);
    }
  };

  const updateSubscriptionConfigForUser = async (config: SubscriptionData) => {
    try {
      const res = await updateSubscriptionConfig(config);
      if (res.status === 200) {
        enqueueSnackbar('Subscription config updated successfully');
        return true;
      } else {
        throw new Error('Failed to update subscription config');
      }
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const loadOptInMessage = async () => {
    try {
      const response = await getOptInMessage();

      if (response.data?.opt_in_message) {
        setOptInMessage(response.data?.opt_in_message);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const changeOptInMessageEnabled = async (enabled: boolean, customMessage: string) => {
    const response = await setOptInMessageStatusService(enabled, customMessage);
    if (!response.success) {
      enqueueSnackbar('Failed to update opt-in message!', { variant: 'error' });
      return { success: false, error: 'Failed to update opt-in message!' };
    }

    setOptInMessageEnabled(enabled);
    setOptInMessage(customMessage);
    return { success: true };
  };

  const handleResetOptInMessage: HandleResetOptInMessage = async () => {
    try {
      const response = await resetOptInMessage();
      if (response.status === 200) {
        setOptInMessage(response.data.opt_in_message);
        return { success: true };
      } else {
        return { success: false, error: response.statusText || 'Unknown error' };
      }
    } catch (error) {
      console.error('Error resetting opt-in message:', error);
      return { success: false, error: 'Failed to reset opt-in message' };
    }
  };

  const checkMessageUsage = async () => {
    setMessageUsageLoading(true);
    try {
      const response = await checkCurrentMessageUsage();
      setMessageUsage(response.data.current_month_count);
    } catch (error) {
      console.error('Error fetching message usage:', error);
      enqueueSnackbar('Failed to fetch message usage', { variant: 'error' });
      throw new Error('Failed to fetch message usage');
    } finally {
      setMessageUsageLoading(false);
    }
  };

  const changeIsAngryCheckDisabled = async (disabled: boolean) => {
    setIsAngryCheckDisabled(disabled);
    const res = await updateIsAngryCheckDisabled(disabled);
    if (res.status === 200) {
      return { success: true };
    } else {
      return { success: false, error: res.data.detail };
    }
  };

  const changeProcessingConfig: ChangeProcessingConfig = async (key, value) => {
    const updatedProcessingConfig = { ...processingConfig, [key]: value };
    setProcessingConfig(updatedProcessingConfig);

    const res = await updateProcessingConfig(updatedProcessingConfig);

    if (res.status === 200) {
      return { success: true };
    } else {
      console.error(res);
      return { success: false, error: res.statusText };
    }
  };

  const changeCustomConfig: ChangeCustomConfig = async (key, value) => {
    const res = await updateCustomConfig(key, value);
    if (res.status === 200) {
      setCustomConfig((prev) => ({ ...prev, [key]: value }));
      return { success: true };
    } else {
      return { success: false, error: res.data.detail };
    }
  };

  const removeServiceAuth: RemoveServiceAuth = async (serviceName: string) => {
    try {
      const res = await removeServiceAuthRequest(serviceName);
      if (res.status !== 200) {
        const errorMessage = res.data.detail || res.data.message || 'Failed to remove service auth';
        enqueueSnackbar(errorMessage, { variant: 'error' });
        return { success: false, error: errorMessage };
      }
      enqueueSnackbar('Service auth removed successfully');
      setHasZendeskToken(false);
      return { success: true };
    } catch (err) {
      console.log(err);
      return { success: false, error: 'Failed to remove service auth' };
    }
  };

  const changeMultiAccountConfig: ChangeMultiAccountConfig = async (key, value) => {
    const updatedMultiAccountConfig = { ...multiAccountConfig, [key]: value };
    setMultiAccountConfig(updatedMultiAccountConfig);

    const res = await updateAccountMultiAccountConfigUser(key, value);

    if (res.status === 200) {
      const message = `Multi account config updated: ${key}`;
      enqueueSnackbar(message, { variant: 'success' });
      return { success: true };
    } else {
      enqueueSnackbar('Failed to update multi account config', { variant: 'error' });
      return { success: false, error: res.data.detail };
    }
  };

  return {
    accountLoading,
    subdomain,
    apiEmailInput,
    apiKeyInput,
    apiKey,
    configSaveErr,
    configSaveLoading,
    configSaveSuccess,
    subdomainDraft,
    setSubdomainDraft,
    modalLoading,
    showSubModal,
    setShowSubModal,
    connectorLoading,
    confirmationModalOpen,
    setConfirmationModalOpen,
    billingLoading,
    billingInfo,
    billingSaveLoading,
    language,
    langSaveLoading,
    langSaveSuccess,
    stealthModeOn,
    setStealthModeOn,
    isStealthEmailOn,
    setIsStealthEmailOn,
    stealthEmail,
    saveStealthError,
    stealthSaveLoading,
    stealthSaveSuccess,
    webhookUrl,
    isProcessingDisabled,
    saveZendeskConfig,
    handleAddSubdomainClick,
    handleEditSubdomainClick,
    handleConnectClick,
    handleCompanyInfoChange,
    handleApiKeyChange,
    handleApiEmailChange,
    handleSaveCompanyInfoClick,
    changeSelectedLanguage,
    saveLanguage,
    saveStealthModeSettings,
    changeIsProcessingDisabled,
    getZendeskAuthUrl,
    zendeskOAuthUrl,
    authUrlLoading,
    authUrlError,
    hasZendeskToken,
    refundRequireImage,
    updateSubscriptionConfigForUser,
    actionsConfig,
    changeActionsConfig,
    enableReplaceExchange,
    enableSameItemReplacement,
    vouchersEnabled,
    manualVoucherApprovalOn,
    optInMessageEnabled,
    optInMessage,
    changeOptInMessageEnabled,
    handleResetOptInMessage,
    messageUsage,
    messageUsageLoading,
    checkMessageUsage,
    getZendeskSubdomain,
    isOrderCancelActionOn,
    isCancelApprovalOn,
    isShopifyMarket,
    isAngryCheckDisabled,
    changeIsAngryCheckDisabled,
    shopifyBillingSettings,
    processingConfig,
    changeProcessingConfig,
    customConfig,
    changeCustomConfig,
    removeServiceAuth,
    multiAccountConfig,
    changeMultiAccountConfig
  };
};

export default useAccountSettings;
